import React, { useEffect, useMemo, useState } from 'react';
import { bondReadyAPI } from '../../../services/api';
import { formatProjectDate, formatScenarioCurrency, sameProjectContext } from './scenarioModelPresentation';
import { resolveLocalTestRunId } from '../../../localE2eAuth';
import { bondRequestSubmittedAccessCopy, customerFacingPartnerDisplayName } from '../../bondready/bondreadyPresentation';

const STEPS = ['review', 'authorize', 'submit', 'done'];

function SummaryRow({ label, value, badge }) {
  if (value == null || value === '') return null;
  return (
    <div className="sm2-ph-row">
      <span className="sm2-ph-key">
        {label}
        {badge ? <span className={`sm2-badge ${badge.className}`} style={{ marginLeft: 6 }}>{badge.label}</span> : null}
      </span>
      <span className="sm2-ph-val">{value}</span>
    </div>
  );
}

const PROJECT_SOURCE_LABEL = 'Entered in Scenario Modeling (customer-provided, not imported accounting data)';

function ProjectContextRows({ context, money }) {
  if (!context) return null;
  return (
    <>
      <SummaryRow label="Project" value={context.projectName} />
      <SummaryRow label="Project type" value={context.projectType} />
      <SummaryRow label="Contract value" value={money(context.contractValue)} />
      <SummaryRow
        label="Requested bond amount"
        value={context.requestedBondAmount != null ? money(context.requestedBondAmount) : 'Not provided'}
      />
      <SummaryRow label="Anticipated start" value={formatProjectDate(context.anticipatedStartDate)} />
      <SummaryRow label="Anticipated completion" value={formatProjectDate(context.anticipatedEndDate)} />
      <SummaryRow label="Source" value={PROJECT_SOURCE_LABEL} />
    </>
  );
}

/**
 * Scenario Modeling → Request a Bond vertical slice.
 * Uses existing BondReady direct-opportunity / Bond request / Gate 2 / visibility
 * contracts. Does not invent production Holmes IDs or financial payloads.
 * The modeled project's context is persisted on the canonical Bond request.
 */
export default function RequestBondJourney({
  orgDisplayName,
  projectContext = null,
  financialAsOfLabel,
  actualsOnly = true,
  disabled = false,
  disabledReason = null,
  decisionSummary = null,
  resultsStale = false,
}) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('review');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [partner, setPartner] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [result, setResult] = useState(null);
  const partnerFacingName = customerFacingPartnerDisplayName(partner?.partnerDisplayName);

  const localTestRunId = useMemo(() => resolveLocalTestRunId(), []);

  const projectName = projectContext?.projectName || null;
  const scenarioKey = projectContext?.scenarioKey || null;
  const sourceRecordId = useMemo(() => {
    const projectPart = (projectName || 'project').trim().toLowerCase().replace(/\s+/g, '-');
    const scenarioPart = scenarioKey || 'base';
    const base = `scenario-bond-request:${scenarioPart}:${projectPart}`;
    return localTestRunId ? `${base}:${localTestRunId}` : base;
  }, [projectName, scenarioKey, localTestRunId]);

  useEffect(() => {
    if (disabled && open) {
      setOpen(false);
    }
  }, [disabled, open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await bondReadyAPI.getConfiguredBondPartner();
        if (!cancelled) setPartner(response.data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.error
              || 'Configured Bond request partner is not available for this environment.'
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const start = () => {
    if (disabled) return;
    setOpen(true);
    setStep('review');
    setError(null);
    setAuthorized(false);
    setResult(null);
  };

  const runSubmit = async () => {
    if (!authorized) {
      setError('Authorization is required before submit.');
      return;
    }
    if (!projectContext) {
      setError('Add and model the project you want bonded before Request Bond.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const opportunityResponse = await bondReadyAPI.createDirectCustomer({
        opportunityType: 'BOND',
        sourceSystem: 'OLYMPIATECH_CUSTOMER_PORTAL',
        sourceRecordId,
        sourceSnapshotContext: 'customer-initiated-bond-request',
        sourceSnapshotAsOf: (() => {
          const raw = typeof financialAsOfLabel === 'string' ? financialAsOfLabel.trim() : '';
          if (!raw) return undefined;
          const isoDay = raw.match(/^(\d{4}-\d{2}-\d{2})/);
          if (isoDay) return `${isoDay[1]}T00:00:00.000Z`;
          const parsed = Date.parse(raw);
          if (Number.isNaN(parsed)) return undefined;
          const date = new Date(parsed);
          const yyyy = date.getUTCFullYear();
          const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
          const dd = String(date.getUTCDate()).padStart(2, '0');
          return `${yyyy}-${mm}-${dd}T00:00:00.000Z`;
        })(),
        sourceConfidence: 'HIGH',
        dataQualityStatus: 'CURRENT',
        currentOwner: `${orgDisplayName || 'Trade partner'} bond-request coordinator`,
        nextAction: 'Review and authorize broker package',
        gate1NotApplicableReason:
          'Direct customer self-initiation; no GC-sponsored outreach authorization applies.',
        ...(localTestRunId
          ? { note: `LOCAL DEMO QA ${localTestRunId} - demonstration/test data only` }
          : {}),
      });
      const opportunity = opportunityResponse.data;
      const opportunityId = opportunity.id;

      let request;
      try {
        const requestResponse = await bondReadyAPI.createBondRequest(opportunityId, {
          status: 'OPEN',
          stage: 'INTAKE',
          currentOwner: `${orgDisplayName || 'Trade partner'} bond-request coordinator`,
          nextAction: 'Obtain bounded Gate 2 authorization',
          amount: projectContext.requestedBondAmount,
          details: localTestRunId
            ? `LOCAL DEMO QA ${localTestRunId} Customer requests assistance with a bond application.`
            : 'Customer requests assistance with a bond application.',
          projectContext: {
            projectName: projectContext.projectName,
            projectType: projectContext.projectType,
            contractValue: projectContext.contractValue,
            anticipatedStartDate: projectContext.anticipatedStartDate,
            anticipatedEndDate: projectContext.anticipatedEndDate,
            scenarioKey: projectContext.scenarioKey,
          },
        });
        request = requestResponse.data;
      } catch (err) {
        if (err?.response?.status === 409) {
          const refreshed = await bondReadyAPI.getOpportunity(opportunityId);
          request = (refreshed.data.requests || []).find((row) => row.requestType === 'BOND');
          if (!request) throw err;
          if (!sameProjectContext(request.projectContext, projectContext)) {
            throw new Error(
              `A Bond request for "${projectContext.projectName}" already exists in BondReady with different project details. `
              + 'Open the existing request in BondReady, or use a distinct project name.'
            );
          }
        } else {
          throw err;
        }
      }

      const scopeToken = partner?.approvedScopeToken;
      if (!scopeToken) {
        throw new Error('Approved Gate 2 scope token is not configured.');
      }

      try {
        await bondReadyAPI.decideGate2(opportunityId, {
          state: 'AUTHORIZED',
          approvedScope: [scopeToken],
          exclusions: [
            'raw-financial-source-records',
            'credentials',
            'unapproved-documents',
            'unapproved-scenario-outputs',
          ],
          releasePackageVersion: 'local-fixture-v1',
          releasePackageReference: `scenario://${sourceRecordId}`,
          sourceEvidenceReference: `ui://request-bond/authorize/${sourceRecordId}`,
          note: 'Bounded broker notification package authorization from Scenario Modeling.',
        });
      } catch (err) {
        // Gate 2 may already be final for this opportunity; continue if authorized scope already present.
        if (err?.response?.status !== 409) throw err;
        const policy = await bondReadyAPI.getDisclosurePolicy(opportunityId);
        if (!policy.data?.companySpecificExternalDisclosureAllowed
          || !(policy.data.approvedScope || []).includes(scopeToken)) {
          throw err;
        }
      }

      await bondReadyAPI.grantConfiguredBondPartnerVisibility(opportunityId, {
        sourceEvidenceReference: `ui://request-bond/visibility/${sourceRecordId}`,
        note: 'Authorized configured Bond request partner visibility; Gate 2 remains the disclosure gate.',
      });

      const submitted = await bondReadyAPI.submitBondRequest(opportunityId, request.id, {
        sourceEvidenceReference: `ui://request-bond/submit/${sourceRecordId}`,
      });

      setResult({
        opportunityId,
        requestId: request.id,
        portalPath: submitted.data?.portalPath || `/bondready/${opportunityId}`,
        deliveryState: submitted.data?.notification?.deliveryState
          || submitted.data?.delivery?.deliveryState,
        productionDeliveryEnabled: partner?.productionDeliveryEnabled === true,
        partnerDisplayName: partner?.partnerDisplayName || null,
        persistedProjectContext: request.projectContext || null,
      });
      setStep('done');
    } catch (err) {
      setError(
        err?.response?.data?.error
          || err?.message
          || 'Unable to submit Bond request.'
      );
    } finally {
      setBusy(false);
    }
  };

  const money = (v) => (v == null ? null : formatScenarioCurrency(v, { style: 'full' }));

  if (!open) {
    return (
      <div className="sm2-card" data-testid="request-bond-entry">
        <div className="sm2-card-header">
          <span className="sm2-card-title">Project Capital &amp; Bonding</span>
        </div>
        <p className="sm2-ap-desc" style={{ marginBottom: 12 }}>
          Continue from this scenario into a confirmation-first Bond request. ConstructPay keeps
          {` ${orgDisplayName || 'your organization'} `}
          as the record owner and only notifies the configured surety partner after visibility and
          Gate 2 authorization are both in place.
        </p>

        <div className="sm2-decision-summary" data-testid="request-bond-decision-summary">
          <div className="sm2-proj-subhead">Current project decision summary</div>
          <SummaryRow label="Project" value={decisionSummary?.project || projectName || '—'} />
          <SummaryRow
            label="Contract value"
            value={money(decisionSummary?.contractValue ?? projectContext?.contractValue)}
            badge={{ label: 'Customer-entered', className: 'sm2-badge-assumption' }}
          />
          <SummaryRow
            label="Bonding discussion / request amount"
            value={
              (decisionSummary?.bondingDiscussionAmount ?? projectContext?.requestedBondAmount) != null
                ? `${money(decisionSummary?.bondingDiscussionAmount ?? projectContext?.requestedBondAmount)} — customer-entered discussion amount, not a bondability determination`
                : null
            }
            badge={{ label: 'Discussion amount', className: 'sm2-badge-assumption' }}
          />
          <SummaryRow
            label="Anticipated start"
            value={formatProjectDate(projectContext?.anticipatedStartDate)}
            badge={{ label: 'Customer-entered', className: 'sm2-badge-assumption' }}
          />
          <SummaryRow
            label="Modeled working-capital impact"
            value={money(decisionSummary?.modeledWorkingCapitalImpact)}
            badge={{ label: 'ConstructPay calculation', className: 'sm2-badge-projection' }}
          />
          <SummaryRow
            label="Current committed LOC"
            value={money(decisionSummary?.currentLoc)}
            badge={{ label: 'Source-backed', className: 'sm2-badge-source' }}
          />
          <SummaryRow
            label="Current drawn / utilized"
            value={money(decisionSummary?.locDrawnBalance)}
            badge={{ label: 'Source-backed', className: 'sm2-badge-source' }}
          />
          <SummaryRow
            label="Available LOC capacity"
            value={money(decisionSummary?.availableLocCapacity)}
            badge={{ label: 'ConstructPay calculation', className: 'sm2-badge-projection' }}
          />
          <SummaryRow
            label="Additional capacity needed"
            value={money(decisionSummary?.additionalCapacityNeeded)}
            badge={{ label: 'ConstructPay calculation', className: 'sm2-badge-projection' }}
          />
          <SummaryRow
            label="Modeled target LOC facility"
            value={money(decisionSummary?.recommendedTargetFacility)}
            badge={{ label: 'ConstructPay calculation', className: 'sm2-badge-projection' }}
          />
          <SummaryRow
            label="Illustrative facility after 10% increase"
            value={money(decisionSummary?.illustrativeFacilityAfterIncrease)}
            badge={{ label: 'ConstructPay calculation', className: 'sm2-badge-projection' }}
          />
          <SummaryRow
            label="Incremental increase (illustrative 10%)"
            value={money(decisionSummary?.illustrativeIncrementalIncrease)}
            badge={{ label: 'ConstructPay calculation', className: 'sm2-badge-projection' }}
          />
          <SummaryRow
            label="Remaining uncovered need"
            value={
              decisionSummary?.remainingUncoveredNeed > 0
                ? money(decisionSummary.remainingUncoveredNeed)
                : null
            }
            badge={{ label: 'ConstructPay calculation', className: 'sm2-badge-projection' }}
          />
          <SummaryRow label="Relevant risk state" value={decisionSummary?.riskState} />
          <p className="sm2-ap-desc" style={{ marginTop: 10 }} data-testid="request-bond-review-notice">
            {decisionSummary?.reviewNotice || 'Review what will be shared before anything is sent.'}
          </p>
        </div>

        {disabled || resultsStale ? (
          <div
            className="sm2-inline-error sm2-inline-error-amber"
            data-testid="request-bond-disabled-reason"
            style={{ marginBottom: 12 }}
          >
            <div className="sm2-inline-error-title">Request Bond unavailable</div>
            <div>{disabledReason || 'Recalculation required before Request Bond.'}</div>
          </div>
        ) : null}

        <button
          type="button"
          className="sm2-btn sm2-btn-primary"
          data-testid="request-bond-start"
          onClick={start}
          disabled={disabled}
        >
          Request Bond
        </button>
      </div>
    );
  }

  return (
    <div className="sm2-card" data-testid="request-bond-journey">
      <div className="sm2-card-header">
        <span className="sm2-card-title">Request a Bond</span>
        <button
          type="button"
          className="sm2-card-action"
          onClick={() => setOpen(false)}
          disabled={busy}
        >
          Close
        </button>
      </div>

      <div className="sm2-scenario-tabs" role="tablist" aria-label="Request Bond steps" style={{ marginBottom: 12 }}>
        {STEPS.filter((s) => s !== 'done').map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            className={`sm2-stab${step === s || (step === 'done' && s === 'submit') ? ' active-base' : ''}`}
            disabled
          >
            {s === 'review' ? 'Review' : s === 'authorize' ? 'Authorize' : 'Submit'}
          </button>
        ))}
      </div>

      {error ? (
        <div className="sm2-inline-error sm2-inline-error-red" data-testid="request-bond-error">
          <div className="sm2-inline-error-title">Request blocked</div>
          <div>{error}</div>
        </div>
      ) : null}

      {step === 'review' ? (
        <div data-testid="request-bond-review">
          <div data-testid="request-bond-project-context">
            <div className="sm2-proj-subhead">Project details carried into BondReady</div>
            <p className="sm2-ap-desc" style={{ marginBottom: 8 }}>
              These values are saved on the Bond request and shown to
              {' '}{partner ? partnerFacingName : 'the configured surety partner'}{' '}
              exactly as entered. You will not need to enter them again.
            </p>
            <ProjectContextRows context={projectContext} money={money} />
          </div>
          <div className="sm2-proj-subhead" style={{ marginTop: 12 }}>
            Scenario context for your review (stays in Scenario Modeling)
          </div>
          <SummaryRow
            label="Modeled working-capital impact"
            value={money(decisionSummary?.modeledWorkingCapitalImpact)}
            badge={{ label: 'ConstructPay calculation', className: 'sm2-badge-projection' }}
          />
          <SummaryRow
            label="Current committed LOC"
            value={money(decisionSummary?.currentLoc)}
            badge={{ label: 'Source-backed', className: 'sm2-badge-source' }}
          />
          <SummaryRow
            label="Available LOC capacity"
            value={money(decisionSummary?.availableLocCapacity)}
            badge={{ label: 'ConstructPay calculation', className: 'sm2-badge-projection' }}
          />
          <SummaryRow
            label="Modeled target LOC facility"
            value={money(decisionSummary?.recommendedTargetFacility)}
            badge={{ label: 'ConstructPay calculation', className: 'sm2-badge-projection' }}
          />
          <SummaryRow
            label="Illustrative facility after 10% increase"
            value={money(decisionSummary?.illustrativeFacilityAfterIncrease)}
            badge={{ label: 'ConstructPay calculation', className: 'sm2-badge-projection' }}
          />
          <SummaryRow label="Relevant risk state" value={decisionSummary?.riskState} />
          <div className="sm2-ph-row"><span className="sm2-ph-key">Surety relationship</span><span className="sm2-ph-val">{partnerFacingName}</span></div>
          <div className="sm2-ph-row"><span className="sm2-ph-key">Financial information current through</span><span className="sm2-ph-val">{financialAsOfLabel || 'Source-backed ConstructPay actuals'}</span></div>
          <p className="sm2-ap-desc" style={{ marginTop: 12 }} data-testid="request-bond-review-notice-step">
            Review what will be shared before anything is sent.
            {' '}
            {actualsOnly
              ? 'Review references source-backed ConstructPay information. Modeled scenario outputs are not disclosed by this Bond request package.'
              : 'Review carefully before authorizing disclosure.'}
          </p>
          <button
            type="button"
            className="sm2-btn sm2-btn-primary"
            data-testid="request-bond-continue-authorize"
            onClick={() => setStep('authorize')}
            disabled={!partner}
          >
            Continue to Authorize
          </button>
        </div>
      ) : null}

      {step === 'authorize' ? (
        <div data-testid="request-bond-authorize">
          <p className="sm2-ap-desc">
            I authorize ConstructPay to share the bounded Bond request notification package with
            {' '}{partner ? partnerFacingName : 'the configured surety partner'} for evaluating this
            bonding request. This does not transfer ownership of
            {` ${orgDisplayName || 'your organization'} `}
            records.
          </p>
          <ul className="sm2-ap-desc" style={{ marginBottom: 12 }}>
            <li>Authorized partner visibility (revocable)</li>
            <li>Project details from Review: name, type, contract value, anticipated dates, and requested bond amount</li>
            <li>Minimum broker notification: request exists in ConstructPay</li>
            <li>Authenticated deep link to the canonical BondReady request</li>
            <li>Excluded by default: AR/AP aging, statements, bank data, credentials, raw source records</li>
          </ul>
          <label className="sm2-ap-toggle" style={{ marginBottom: 12 }}>
            <input
              type="checkbox"
              checked={authorized}
              onChange={(e) => setAuthorized(e.target.checked)}
              data-testid="request-bond-authorize-checkbox"
            />
            I authorize this bounded disclosure
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="sm2-btn sm2-btn-ghost" onClick={() => setStep('review')}>Back</button>
            <button
              type="button"
              className={`sm2-btn sm2-btn-primary${!authorized ? ' sm2-btn-blocked' : ''}`}
              data-testid="request-bond-continue-submit"
              disabled={!authorized}
              aria-disabled={!authorized}
              onClick={() => {
                if (!authorized) return;
                setStep('submit');
              }}
            >
              Continue to Submit
            </button>
          </div>
          {!authorized ? (
            <p className="sm2-ap-desc" data-testid="request-bond-authorize-required" style={{ marginTop: 8 }}>
              Check “I authorize this bounded disclosure” to continue.
            </p>
          ) : null}
        </div>
      ) : null}

      {step === 'submit' ? (
        <div data-testid="request-bond-submit">
          <p className="sm2-ap-desc">
            Submit saves the project details from Review on the canonical BondReady Bond request, grants
            configured partner visibility, enforces Gate 2 scope, and queues a policy-gated
            notification.
            {partner?.productionDeliveryEnabled === true
              ? ` ${partnerFacingName} is notified by email with a link to the request.`
              : ' Email delivery is off for this environment.'}
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="sm2-btn sm2-btn-ghost" onClick={() => setStep('authorize')} disabled={busy}>Back</button>
            <button
              type="button"
              className="sm2-btn sm2-btn-primary"
              data-testid="request-bond-submit-button"
              disabled={busy || !authorized}
              onClick={runSubmit}
            >
              {busy ? 'Submitting…' : 'Submit Bond Request'}
            </button>
          </div>
        </div>
      ) : null}

      {step === 'done' && result ? (
        <div data-testid="request-bond-done">
          <div className="sm2-inline-error" style={{ borderColor: 'var(--accent-green)', background: 'transparent' }}>
            <div className="sm2-inline-error-title">Bond request submitted</div>
            <div data-testid="request-bond-submit-confirmation">
              {bondRequestSubmittedAccessCopy({
                partnerDisplayName: result.partnerDisplayName
                  || partner?.partnerDisplayName,
                productionDeliveryEnabled: result.productionDeliveryEnabled,
                deliveryState: result.deliveryState,
              })}
            </div>
          </div>
          {result.persistedProjectContext ? (
            <div data-testid="request-bond-done-project" style={{ marginTop: 12 }}>
              <div className="sm2-proj-subhead">Saved on the BondReady request</div>
              <ProjectContextRows context={result.persistedProjectContext} money={money} />
            </div>
          ) : null}
          <a
            className="sm2-btn sm2-btn-primary"
            href={result.portalPath}
            data-testid="request-bond-open-tracker"
            style={{ display: 'inline-flex', marginTop: 12, textDecoration: 'none' }}
          >
            Open BondReady tracker
          </a>
        </div>
      ) : null}
    </div>
  );
}
